import { CHART_SECONDARY_COLOR, CLS_PREFIX, THEME_COLOR_LIST } from '../constants';
import { getFormattedValue } from '../utils';
import type { ECharts } from 'echarts';
import echarts from '@/utils/echarts';
import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { ColumnType } from '../common/type';
import { isArray } from 'lodash';

type Props = {
  dateColumnName: string;
  metricFields: ColumnType[];
  resultList: any[];
  triggerResize?: boolean;
  chartType?: string;
};

const MultiMetricsTrendChart: React.FC<Props> = ({
  dateColumnName,
  metricFields,
  resultList,
  triggerResize,
  chartType,
}) => {
  const chartRef = useRef<any>(null);
  const [instance, setInstance] = useState<ECharts>();

  const renderChart = () => {
    let instanceObj: any;
    if (!instance) {
      instanceObj = echarts.init(chartRef.current);
      setInstance(instanceObj);
    } else {
      instanceObj = instance;
      instanceObj.clear();
    }

    const xData = resultList?.map((item: any) => {
      const date = isArray(item[dateColumnName])
        ? item[dateColumnName].join('-')
        : `${item[dateColumnName]}`;
      return date.length === 10 ? dayjs(date).format('MM-DD') : date;
    });

    instanceObj.setOption({
      legend: {
        left: 0,
        top: 0,
        icon: 'rect',
        itemWidth: 15,
        itemHeight: 5,
        type: 'scroll',
      },
      xAxis: {
        type: 'category',
        // axisTick: {
        //   alignWithLabel: true,
        //   lineStyle: {
        //     color: CHART_SECONDARY_COLOR,
        //   },
        // },
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: CHART_SECONDARY_COLOR,
          },
        },
        axisLabel: {
          showMaxLabel: true,
          color: '#999',
        },
        data: xData,
      },
      yAxis: {
        type: 'value',
        splitLine: {
          lineStyle: {
            opacity: 0.3,
          },
        },
        axisLabel: {
          formatter: function (value: any) {
            const formattedValue = value === 0 ? 0 : getFormattedValue(value);
            return formattedValue + (metricFields[0]?.unit || '');
          },
        },
        scale: true,
      },
      tooltip: {
        trigger: 'axis',
        formatter: function (params: any[]) {
          const param = params[0];
          const valueLabels = params
            .sort((a, b) => b.value - a.value)
            .map(
              (item: any) =>
                `<div style="margin-top: 3px;">${
                  item.marker
                } <span style="display: inline-block; width: 70px; margin-right: 12px;">${
                  item.seriesName
                }</span><span style="display: inline-block; width: 90px; text-align: right; font-weight: 500;">${
                  item.value === '' ? '-' : getFormattedValue(item.value) + (metricFields[0]?.unit || '')
                }</span></div>`
            )
            .join('');
          return `${param.name}<br />${valueLabels}`;
        },
      },
      grid: {
        left: '1%',
        right: '4%',
        bottom: '3%',
        top: 45,
        containLabel: true,
      },
      series: metricFields.map((metricField, index) => {
        return {
          type: chartType,
          name: metricField.name,
          // symbol: 'none',
          // symbol: 'circle',
          // showSymbol: resultList.length === 1, // showSymbol为false，label标签会全部被隐藏
          // smooth: true, // 启用后，折线图会弯曲显示
          data: resultList.map((item: any) => {
            const value = item[metricField.nameEn];
            return (metricField.dataFormatType === 'percent' ||
              metricField.dataFormatType === 'decimal') &&
              metricField.dataFormat?.needMultiply100
              ? value * 100
              : value;
          }),
          color: THEME_COLOR_LIST[index],
          label: {
            show: true,
            position: 'top',
            formatter: (params: any) => {
              return `${params.value}${metricField.unit || ''}`;
            },
          },
          labelLayout: {
            hideOverlap: true, // 是否隐藏重叠的标签
          },
        };
      }),
    });
    instanceObj.resize();
  };

  useEffect(() => {
    renderChart();
  }, [resultList, chartType]);

  useEffect(() => {
    if (triggerResize && instance) {
      instance.resize();
    }
  }, [triggerResize]);

  return <div style={{ width: '100%', height: '180px' }} ref={chartRef} />;
};

export default MultiMetricsTrendChart;
